import NotARishtaBureau from "../shared/NotARishtaBureau";
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaWhatsapp, FaPhoneAlt, FaShieldAlt, FaUserCheck, FaHeart, FaUsers, FaChevronRight, FaNewspaper, FaUserPlus, FaSearch, FaMosque, FaBrain, FaGraduationCap, FaHome, FaComments, FaHandshake } from "react-icons/fa";
import { HiOutlineShieldCheck } from "react-icons/hi";
import { FaBinoculars } from "react-icons/fa";
import SEO, { serviceSchema, faqSchema, howToSchema } from "../../seo/SEO";
// Note: organizationSchema & websiteSchema are now in static index.html to be visible without JS

import { useSiteSettings } from "../../../utils/SiteSettingsContext";
import baseURL from "../../authComponents/baseURL/BaseURL";
import ProfileVerify from "../main-screen/ProfileVerify";
import { IconBadge, PlaneGraphic, MOVING_ABROAD_GRAPHICS } from "../../reusableComponents/MovingAbroadIcons";
import { useFreeOffered } from "../../../utils/freeOffered";

/* ─── Urdu font readiness hook ────────────────────────────────────────── */
const useUrduFontReady = () => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (typeof document !== "undefined" && document.fonts) {
      // Wait for Noto Naskh Arabic (primary) — fallback to ready after 3s
      const timeout = setTimeout(() => setReady(true), 3000);
      document.fonts.load("16px 'Noto Naskh Arabic'")
        .then(() => { clearTimeout(timeout); setReady(true); })
        .catch(() => { clearTimeout(timeout); setReady(true); });
    } else {
      setReady(true);
    }
  }, []);
  return ready;
};

/* ─── colour palette ──────────────────────────────────────────────────── */
const C = {
  emerald:   "#047857",
  emeraldDk: "#065f46",
  gold:      "#d4a843",
  goldLight: "#f5e6c8",
  cream:     "#fffdf7",
  dark:      "#1a1a2e",
  white:     "#ffffff",
  whatsapp:  "#25D366",
};

/* ─── Urdu News Ticker ────────────────────────────────────────────────── */
const FALLBACK_URDU_HEADLINES = [
  "سلیکٹ پروپوزل — پاکستان کی سب سے قابل اعتماد رشتہ سروس",
  "ابھی رجسٹر کریں اور اپنا بہترین رشتہ تلاش کریں",
  "تمام پروفائلز تصدیق شدہ — محفوظ اور قابل اعتماد",
  "پاکستان بھر سے تعلیم یافتہ رشتے — آج ہی تلاش شروع کریں",
  "آپ کی پرائیویسی ہماری ترجیح — مکمل کنٹرول آپ کے ہاتھ میں",
  "✈️ نیا فیچر: بیرون ملک جانے والوں کے لیے رشتہ میچنگ — ابھی رجسٹر کریں",
  "🌍 باہر جا رہے ہیں؟ اپنی منزل کے شہر میں رشتہ ڈھونڈیں",
];

/* Evergreen SP promo lines — only shown as fallback when live RSS fails */
const FUN_TICKER_LINES = [
  "💡 کیا آپ جانتے ہیں؟ سلیکٹ پروپوزل پاکستان کا واحد پلیٹ فارم ہے جو نفسیاتی ہم آہنگی استعمال کرتا ہے",
  "🚀 AI سے رشتے ڈھونڈیں — سلیکٹ پروپوزل اس میدان میں سب سے آگے",
  "✈️ بیرون ملک جا رہے ہیں؟ اپنی منزل کے شہر میں رشتہ ڈھونڈیں — نیا فیچر لائیو",
  "💍 سنجیدہ رشتے، تصدیق شدہ پروفائلز — ابھی رجسٹر کریں",
  "🌍 دنیا بھر میں پاکستانی رشتے — آسٹریلیا، کینیڈا، UK اور مزید",
  "🏏 پاکستان کرکٹ — قوم کا فخر، دنیا بھر میں شائقین",
  "🎓 ہزاروں تعلیم یافتہ نوجوان سلیکٹ پروپوزل پر رشتہ تلاش کر رہے ہیں",
  "🏆 پاکستانی نوجوان عالمی سطح پر نام روشن کر رہے ہیں",
];

/*
 * Pakistan news RSS feeds (Urdu) — weighted towards Sports & Entertainment.
 * Each entry has a `weight` that controls how many headlines we keep from it.
 * NOTE: Jang & Geo dropped Urdu RSS — only Express & BBC Urdu work (Jul 2026).
 */
const PK_NEWS_FEEDS = [
  // 🏏 Sports (heavy)
  { url: "https://www.express.pk/category/sports/feed/", weight: 5 },     // Express Sports
  { url: "https://feeds.bbci.co.uk/urdu/sport/rss.xml",  weight: 4 },    // BBC Urdu Sports
  // 📰 Pakistan & World news (medium)
  { url: "https://www.express.pk/feed/",                  weight: 3 },    // Express Latest
  { url: "https://feeds.bbci.co.uk/urdu/rss.xml",         weight: 3 },   // BBC Urdu Main
  { url: "https://www.express.pk/category/pakistan/feed/", weight: 2 },   // Express Pakistan
  { url: "https://www.express.pk/category/world/feed/",   weight: 2 },   // Express World
  // 💻 Science & Tech (light)
  { url: "https://feeds.bbci.co.uk/urdu/science/rss.xml",  weight: 3 }, // BBC Urdu Sci/Tech
];

/* Fetch live weather for 2 major Pakistani cities only (to keep ticker varied) */
const PK_WEATHER_CITIES = ["Karachi", "Lahore"];
const WEATHER_CITY_URDU = {
  Karachi: "کراچی", Lahore: "لاہور", Islamabad: "اسلام آباد",
  Peshawar: "پشاور", Quetta: "کوئٹہ",
};
const WEATHER_DESC_URDU = {
  Clear: "صاف", Clouds: "ابر آلود", Rain: "بارش", Drizzle: "ہلکی بارش",
  Thunderstorm: "طوفانی بارش", Snow: "برف باری", Mist: "دھند", Haze: "دھندلا",
  Fog: "گہری دھند", Dust: "گرد آلود", Smoke: "دھواں",
};
async function fetchWeatherHeadlines() {
  try {
    const results = await Promise.allSettled(
      PK_WEATHER_CITIES.map(async (city) => {
        const res = await fetch(`https://wttr.in/${city}?format=j1`, {
          signal: AbortSignal.timeout(5000),
          headers: { "Accept-Language": "ur" },
        });
        const d = await res.json();
        const cur = d?.current_condition?.[0];
        if (cur) {
          const urduCity = WEATHER_CITY_URDU[city] || city;
          const desc = cur.lang_ur?.[0]?.value || WEATHER_DESC_URDU[cur.weatherDesc?.[0]?.value] || cur.weatherDesc?.[0]?.value || "";
          const temp = cur.temp_C;
          const humidity = cur.humidity;
          return `🌤️ ${urduCity}: ${desc}، \u200E${temp}°C\u200F، نمی \u200E${humidity}%\u200F`;
        }
        return null;
      })
    );
    return results.filter((r) => r.status === "fulfilled" && r.value).map((r) => r.value);
  } catch {
    return [];
  }
}

const TICKER_CACHE_KEY = "sp_ticker_headlines";
const TICKER_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const UrduNewsTicker = () => {
  // Load cached headlines immediately, fall back to static defaults
  const [headlines, setHeadlines] = useState(() => {
    try {
      const cached = JSON.parse(localStorage.getItem(TICKER_CACHE_KEY));
      if (cached && cached.headlines?.length && Date.now() - cached.ts < TICKER_CACHE_TTL) {
        return cached.headlines;
      }
    } catch { /* ignore */ }
    return FALLBACK_URDU_HEADLINES;
  });

  useEffect(() => {
    const fetchWithTimeout = (url, ms = 5000) => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), ms);
      return fetch(url, { signal: controller.signal }).finally(() => clearTimeout(timer));
    };

    const fetchNews = async () => {
      const allHeadlines = [];

      // Fetch RSS feeds with per-feed weight limiting
      const feedPromises = PK_NEWS_FEEDS.map(async ({ url: feedUrl, weight }) => {
        try {
          const res = await fetchWithTimeout(
            "https://api.rss2json.com/v1/api.json?rss_url=" +
              encodeURIComponent(feedUrl),
            5000
          );
          const data = await res.json();
          if (data.status === "ok" && data.items?.length) {
            const titles = data.items
              .map((item) => item.title?.replace(/<[^>]*>/g, "").trim())
              .filter((t) => t && /[\u0600-\u06FF]/.test(t));
            // Respect weight — take at most `weight` headlines per feed
            return titles.slice(0, weight);
          }
        } catch {
          // Skip failed / timed-out feed
        }
        return [];
      });

      // Fetch weather headlines in parallel
      const [feedResults, weatherLines] = await Promise.all([
        Promise.allSettled(feedPromises),
        fetchWeatherHeadlines(),
      ]);

      feedResults.forEach((r) => {
        if (r.status === "fulfilled" && r.value?.length) {
          allHeadlines.push(...r.value);
        }
      });
      // Sprinkle weather lines in (max 2 cities now)
      allHeadlines.push(...weatherLines);

      const unique = [...new Set(allHeadlines)];
      if (unique.length >= 3) {
        // Live news loaded — use only real headlines (no static promo lines)
        const shuffled = unique.sort(() => Math.random() - 0.5).slice(0, 25);
        setHeadlines(shuffled);
        try {
          localStorage.setItem(TICKER_CACHE_KEY, JSON.stringify({ headlines: shuffled, ts: Date.now() }));
        } catch { /* quota exceeded — ignore */ }
      } else {
        // RSS failed — fall back to static promo lines
        const fallback = [...FUN_TICKER_LINES].sort(() => Math.random() - 0.5);
        setHeadlines(fallback);
      }
    };
    fetchNews();
    const interval = setInterval(fetchNews, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Duplicate headlines so the ticker loops seamlessly
  const tickerText = [...headlines, ...headlines];
  const trackRef = useRef(null);

  // JS-driven animation via requestAnimationFrame — works reliably on
  // mobile SPA navigation, no CSS @keyframes or <style> injection needed.
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    let raf;
    let offset = 0;
    const speed = 0.55; // px per frame (~33px/s at 60fps)

    const tick = () => {
      offset += speed;
      const halfWidth = el.scrollWidth / 2;
      if (offset >= halfWidth) offset -= halfWidth;
      // Move L→R: start at -halfWidth, scroll toward 0, then wrap
      el.style.transform = `translate3d(${-halfWidth + offset}px, 0, 0)`;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [headlines]);

  const urduFont = "'Noto Naskh Arabic', 'Noto Nastaliq Urdu', serif";

  return (
    <div className="bg-emerald-900 text-white py-1 overflow-hidden" style={{ WebkitOverflowScrolling: "touch" }}>
      <div className="flex items-center" style={{ direction: "rtl" }}>
        <span className="bg-amber-500 text-emerald-900 font-bold text-[10px] px-2 py-0.5 mr-2 ml-1 rounded flex-shrink-0" style={{ fontFamily: urduFont }}>
          تازہ خبریں
        </span>
        <div className="overflow-hidden flex-1 relative" style={{ direction: "ltr", minWidth: 0 }}>
          <div ref={trackRef} className="whitespace-nowrap text-xs md:text-sm" style={{ display: "inline-block", width: "max-content", direction: "rtl", fontFamily: urduFont, WebkitFontSmoothing: "antialiased", MozOsxFontSmoothing: "grayscale", willChange: "transform", backfaceVisibility: "hidden" }}>
            {tickerText.map((h, i) => (
              <span key={i} className="mx-4 md:mx-6">
                {h}
                <span className="text-amber-400 mx-1.5 md:mx-2" style={{ fontSize: "0.6em", verticalAlign: "middle" }}>◆</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Hero ────────────────────────────────────────────────────────────── */
const HeroSection = () => {
  const navigate = useNavigate();

  return (
    <header
      className="relative text-white overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${C.emerald} 0%, ${C.emeraldDk} 50%, ${C.dark} 100%)`,
        minHeight: "560px",
      }}
    >
      {/* decorative Islamic pattern overlay */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      {/* ── Left couple (bridal) ── */}
      <div
        className="hidden lg:block absolute bottom-0 left-0 pointer-events-none w-[28%]"
        style={{ maxWidth: "420px", zIndex: 1 }}
      >
        <img
          src="/images/hero/couple-left.webp"
          alt=""
          className="w-full h-auto block"
          style={{
            filter: "brightness(0.92) saturate(0.85) contrast(1.08)",
            maskImage: "linear-gradient(to right, rgba(0,0,0,1) 50%, rgba(0,0,0,0) 100%), linear-gradient(to top, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 8%)",
            WebkitMaskImage: "linear-gradient(to right, rgba(0,0,0,1) 50%, rgba(0,0,0,0) 100%), linear-gradient(to top, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 8%)",
            maskComposite: "intersect",
            WebkitMaskComposite: "source-in",
          }}
          loading="eager"
        />
      </div>

      {/* ── Right couple (modern) ── */}
      <div
        className="hidden lg:block absolute bottom-0 right-0 pointer-events-none w-[26%]"
        style={{ maxWidth: "400px", zIndex: 1 }}
      >
        <img
          src="/images/hero/couple-right.webp"
          alt=""
          className="w-full h-auto block"
          style={{
            filter: "brightness(0.92) saturate(0.85) contrast(1.08)",
            maskImage: "linear-gradient(to left, rgba(0,0,0,1) 50%, rgba(0,0,0,0) 100%), linear-gradient(to top, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 8%)",
            WebkitMaskImage: "linear-gradient(to left, rgba(0,0,0,1) 50%, rgba(0,0,0,0) 100%), linear-gradient(to top, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 8%)",
            maskComposite: "intersect",
            WebkitMaskComposite: "source-in",
          }}
          loading="eager"
        />
      </div>

      {/* ── golden radial glow in center ── */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: "30%", left: "50%", transform: "translate(-50%, -30%)",
          width: "900px", height: "900px",
          background: "radial-gradient(circle, rgba(212,168,67,0.06) 0%, transparent 70%)",
          zIndex: 0,
        }}
      />

      <div className="relative max-w-[700px] mx-auto px-5 pt-8 pb-8 text-center" style={{ zIndex: 2 }}>
        {/* badge */}
        <div className="mb-5">
          <span className="inline-block text-[11px] md:text-xs font-bold tracking-[0.18em] uppercase" style={{ color: C.gold }}>
            PAKISTAN'S #1 TRUSTED MATRIMONIAL SERVICE
          </span>
        </div>

        {/* Urdu headline — bigger */}
        <h1
          className="text-3xl md:text-4xl lg:text-5xl font-semibold mb-5"
          style={{ fontFamily: "'Noto Naskh Arabic', serif", textShadow: "0 2px 20px rgba(0,0,0,0.3)" }}
        >
          اپنا بہترین رشتہ تلاش کریں
        </h1>

        {/* English headline */}
        <h2 className="font-playfair text-2xl md:text-4xl font-bold mb-4 text-white/95" style={{ textShadow: "0 2px 15px rgba(0,0,0,0.2)" }}>
          Find Your Perfect Rishta Today
        </h2>

        <p className="text-base md:text-lg max-w-[650px] mx-auto mb-8 text-white/85 leading-relaxed" style={{ textShadow: "0 1px 8px rgba(0,0,0,0.2)" }}>
          Pakistan's most trusted, verified matrimonial service.<br className="hidden md:block" />
          Simple. Safe. Family-approved.
        </p>

        {/* Self-service clarifier */}
        <p
          className="text-sm md:text-base max-w-[620px] mx-auto mb-8 font-semibold"
          style={{ color: C.gold, textShadow: "0 1px 8px rgba(0,0,0,0.25)" }}
        >
          You search and connect yourself — we verify profiles.<br />
          <span style={{ fontFamily: "'Noto Naskh Arabic', serif" }}>آپ خود تلاش کر کے رابطہ کرتے ہیں — ہم پروفائلز کی تصدیق کرتے ہیں۔</span>
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
          <Link
            to="/signup"
            className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full font-semibold text-base transition-all duration-300 border-2 border-white/40 hover:border-white/80 text-white"
          >
            ابھی رجسٹر کریں — Register Quick
            <FaChevronRight className="text-xs" />
          </Link>
          <Link
            to="/all-profiles"
            className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full font-semibold text-base transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.03]"
            style={{ backgroundColor: C.gold, color: C.dark }}
          >
            تمام پروفائلز دیکھیں — View All Profiles
          </Link>
        </div>

        {/* Stats row */}
        <div className="flex justify-center gap-10 md:gap-16 flex-wrap pb-4 md:pb-6">
          {[
            { num: "24/7", labelUr: "خود سروس", labelEn: "Self-Service" },
            { num: "100%", labelUr: "دستی جانچ", labelEn: "Manually Screened" },
            { num: "15+", labelUr: "ممالک", labelEn: "Countries" },
          ].map((s, i) => (
            <div key={i} className="text-center">
              <span className="block text-3xl md:text-4xl font-bold" style={{ color: C.gold, textShadow: "0 2px 10px rgba(0,0,0,0.2)" }}>
                {s.num}
              </span>
              <span className="block text-xs uppercase tracking-wider opacity-80 mt-1">
                {s.labelEn}
              </span>
              <span className="block text-lg md:text-xl opacity-70 mt-1 font-urdu">
                {s.labelUr}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Couples on phones/tablets: their own row under the stats, so the
          text never sits on top of the photos (desktop uses the side images). */}
      <div className="lg:hidden relative flex justify-center gap-6 items-end px-1 -mt-10 pointer-events-none" style={{ zIndex: 1 }}>
        {[
          { src: "/images/hero/couple-left.webp", side: "to right" },
          { src: "/images/hero/couple-right.webp", side: "to left" },
        ].map(({ src, side }) => (
          <img
            key={src}
            src={src}
            alt=""
            className="w-[45%] max-w-[230px] h-auto block"
            style={{
              filter: "brightness(0.92) saturate(0.85) contrast(1.08)",
              maskImage: `linear-gradient(${side}, rgba(0,0,0,1) 70%, rgba(0,0,0,0) 100%), linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 22%)`,
              WebkitMaskImage: `linear-gradient(${side}, rgba(0,0,0,1) 70%, rgba(0,0,0,0) 100%), linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 22%)`,
              maskComposite: "intersect",
              WebkitMaskComposite: "source-in",
            }}
            loading="eager"
          />
        ))}
      </div>

      {/* wave divider */}
      <div className="absolute bottom-0 left-0 right-0" style={{ zIndex: 3 }}>
        <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path d="M0 60L60 50C120 40 240 20 360 15C480 10 600 20 720 25C840 30 960 30 1080 25C1200 20 1320 10 1380 5L1440 0V60H0Z" fill={C.cream} />
        </svg>
      </div>
    </header>
  );
};

/* ─── Trust Badges ────────────────────────────────────────────────────── */
const TrustBadges = () => (
  <section className="py-5 md:py-6" style={{ backgroundColor: C.cream }}>
    <div className="max-w-[900px] mx-auto px-5">
      <div className="text-center mb-4">
        <span
          className="block text-2xl md:text-3xl font-semibold mb-3 font-urdu" style={{ color: C.emerald }}
        >
          مصدقہ اور رجسٹرڈ
        </span>
        <h3 className="font-playfair text-2xl md:text-3xl font-bold text-[#222]">
          Certified & Government Registered
        </h3>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-[700px] mx-auto">
        {/* Govt of Pakistan Badge */}
        <div className="flex items-center gap-4 bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex-shrink-0 w-14 h-14 rounded-lg flex items-center justify-center"
               style={{ backgroundColor: '#f0fdf4' }}>
            <svg width="42" height="42" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
              <path d="M30 95 Q20 70 28 50 Q22 60 18 75 Q15 85 25 95Z" fill="#01411C" opacity="0.7"/>
              <path d="M25 90 Q18 65 25 45 Q20 55 16 70 Q14 80 22 92Z" fill="#01411C" opacity="0.5"/>
              <path d="M90 95 Q100 70 92 50 Q98 60 102 75 Q105 85 95 95Z" fill="#01411C" opacity="0.7"/>
              <path d="M95 90 Q102 65 95 45 Q100 55 104 70 Q106 80 98 92Z" fill="#01411C" opacity="0.5"/>
              <path d="M35 30 L85 30 L85 70 Q85 90 60 100 Q35 90 35 70Z" fill="#01411C" stroke="#01411C" strokeWidth="2"/>
              <circle cx="55" cy="60" r="16" fill="#fff"/>
              <circle cx="59" cy="57" r="13" fill="#01411C"/>
              <polygon points="72,52 74,58 80,58 75,62 77,68 72,64 67,68 69,62 64,58 70,58" fill="#fff"/>
              <rect x="32" y="96" width="56" height="8" rx="2" fill="#01411C"/>
              <text x="60" y="103" textAnchor="middle" fill="#fff" fontSize="5.5" fontWeight="bold" fontFamily="serif">PAKISTAN</text>
            </svg>
          </div>
          <div>
            <p className="font-bold text-[#222] text-sm">Government Registered</p>
            <p className="text-base mt-0.5 font-urdu" style={{ color: '#666' }}>
              حکومت پاکستان سے رجسٹرڈ
            </p>
          </div>
        </div>

        {/* FBR Badge */}
        <div className="flex items-center gap-4 bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex-shrink-0 w-14 h-14 rounded-lg flex items-center justify-center"
               style={{ backgroundColor: '#f0fdf4' }}>
            <svg width="40" height="40" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="5" y="5" width="90" height="90" rx="12" fill="#1a5276" />
              <text x="50" y="45" textAnchor="middle" fill="#fff" fontSize="22" fontWeight="bold" fontFamily="serif">FBR</text>
              <line x1="25" y1="55" x2="75" y2="55" stroke="#f1c40f" strokeWidth="2" />
              <text x="50" y="72" textAnchor="middle" fill="#f1c40f" fontSize="8" fontWeight="bold">REGISTERED</text>
            </svg>
          </div>
          <div>
            <p className="font-bold text-[#222] text-sm">FBR Registered</p>
            <p className="text-base mt-0.5 font-urdu" style={{ color: '#666' }}>
              ایف بی آر سے رجسٹرڈ
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ─── YouTube Video Section ───────────────────────────────────────────── */
const YouTubeVideoSection = () => (
  <section className="py-8 md:py-12 bg-white">
    <div className="max-w-[900px] mx-auto px-5">
      <div className="relative w-full overflow-hidden rounded-2xl shadow-lg" style={{ paddingBottom: "56.25%" }}>
        <iframe
          className="absolute inset-0 w-full h-full"
          src="https://www.youtube.com/embed/g6eehy1PtUs?rel=0"
          title="Select Proposal — Pakistan's #1 Trusted Matrimonial Service"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          loading="lazy"
        />
      </div>
    </div>
  </section>
);

/* ─── Features (simplified) ──────────────────────────────────────────── */
const FeaturesSection = () => {
  const features = [
    {
      icon: <FaUserCheck />,
      titleEn: "Verified Profiles",
      titleUr: "تصدیق شدہ پروفائلز",
      desc: "Every profile is manually verified. No fakes, no time waste.",
      color: C.emerald,
      bg: "#ecfdf5",
    },
    {
      icon: <FaUsers />,
      titleEn: "Family Values",
      titleUr: "خاندانی اقدار",
      desc: "Built for Pakistani families. Education, profession, sect and city — all filters included.",
      color: "#7c3aed",
      bg: "#f5f3ff",
    },
    {
      icon: <FaShieldAlt />,
      titleEn: "100% Private & Safe",
      titleUr: "مکمل طور پر محفوظ",
      desc: "Your data stays secure. Contact info only shared when you choose.",
      color: "#0891b2",
      bg: "#ecfeff",
    },
    {
      icon: <FaHeart />,
      titleEn: "Serious Rishtas Only",
      titleUr: "صرف سنجیدہ رشتے",
      desc: "No dating. Only families looking for genuine marriage proposals.",
      color: "#dc2626",
      bg: "#fef2f2",
    },
  ];

  return (
    <section className="py-6 md:py-8 bg-white">
      <div className="max-w-[1000px] mx-auto px-5">
        <div className="text-center mb-5">
          <span
            className="block text-xl md:text-2xl font-semibold mb-3 font-urdu" style={{ color: C.emerald }}
          >
            ہمیں کیوں چنیں؟
          </span>
          <h2 className="font-playfair text-3xl md:text-[2.4rem] font-bold text-[#222]">
            Why Select Proposal?
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {features.map((f, i) => (
            <div
              key={i}
              className="rounded-xl p-5 transition-all duration-300 hover:translate-y-[-3px] hover:shadow-md"
              style={{ backgroundColor: f.bg }}
            >
              <div
                className="w-11 h-11 rounded-lg flex items-center justify-center text-white text-lg mb-3"
                style={{ backgroundColor: f.color }}
              >
                {f.icon}
              </div>
              <h3 className="font-playfair text-lg font-bold text-[#222] mb-1">{f.titleEn}</h3>
              <p className="text-base mb-2 font-urdu" style={{ color: f.color }}>
                {f.titleUr}
              </p>
              <p className="text-[#555] text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── Compatibility Assessment ─────────────────────────────────────────── */
const CompatibilitySection = () => {
  const dimensions = [
    { Icon: FaMosque, titleEn: "Deen & Spiritual Values", titleUr: "دین اور روحانی اقدار", desc: "Matched on religiosity, prayer habits, and spiritual outlook — the foundation of a Muslim household.", accent: "#6ee7b7" },
    { Icon: FaBrain, titleEn: "Personality & Temperament", titleUr: "شخصیت اور مزاج", desc: "Communication style, emotional balance, and conflict approach — no other Pakistani platform considers this.", accent: "#c4b5fd" },
    { Icon: FaGraduationCap, titleEn: "Education & Ambition", titleUr: "تعلیم اور عزائم", desc: "How education level, career goals, and intellectual curiosity align between two profiles.", accent: "#67e8f9" },
    { Icon: FaHome, titleEn: "Family & Lifestyle Fit", titleUr: "خاندانی اور طرز زندگی", desc: "Living arrangements, lifestyle preferences, and cultural values that shape daily married life.", accent: "#fca5a5" },
    { Icon: FaComments, titleEn: "Interests & Daily Life", titleUr: "دلچسپیاں اور روزمرہ", desc: "Shared hobbies, leisure preferences, and routines — the small things that keep couples connected.", accent: "#fde68a" },
    { Icon: FaHandshake, titleEn: "Marriage Readiness", titleUr: "شادی کی تیاری", desc: "Are you both emotionally and practically ready at the same time? No more wasted conversations.", accent: "#86efac" },
  ];

  return (
    <section className="py-8 md:py-12" style={{ background: "linear-gradient(135deg, #047857 0%, #065f46 30%, #1e3a5f 70%, #312e81 100%)" }}>
      <div className="max-w-[1000px] mx-auto px-5">
        {/* Header */}
        <div className="text-center mb-6">
          <span className="inline-block text-[11px] md:text-xs font-bold tracking-[0.16em] uppercase mb-1" style={{ color: "#fbbf24" }}>
            PAKISTAN'S FIRST & ONLY
          </span>
          <h3 className="font-playfair text-2xl md:text-[2.2rem] font-bold text-white mb-2">
            Psychological Compatibility Assessment
          </h3>
          <p className="text-gray-200 text-sm md:text-base max-w-[650px] mx-auto leading-relaxed">
            Most rishta platforms stop at age, biodata, and city. We go deeper — using <strong className="text-white">real psychology</strong> to measure what actually makes marriages work.
          </p>
        </div>

        {/* Dimension cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5 mb-8">
          {dimensions.map((d, i) => (
            <div
              key={i}
              className="rounded-xl p-4 md:p-5 text-center transition-all duration-300 hover:translate-y-[-2px] hover:shadow-lg border border-white/20"
              style={{ backgroundColor: "rgba(255,255,255,0.12)", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)" }}
            >
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center mx-auto mb-2" style={{ backgroundColor: d.accent + "22" }}>
                <d.Icon className="text-xl md:text-2xl" style={{ color: d.accent }} />
              </div>
              <h4 className="font-playfair text-sm md:text-base font-bold text-white mb-0.5">{d.titleEn}</h4>
              <p className="text-xs md:text-sm mb-1.5 font-urdu" style={{ color: d.accent }}>{d.titleUr}</p>
              <p className="text-gray-300 text-xs leading-relaxed hidden sm:block">{d.desc}</p>
            </div>
          ))}
        </div>

        {/* Pakistan-first callout */}
        <div className="rounded-xl border border-white/20 p-5 md:p-6 text-center" style={{ backgroundColor: "rgba(255,255,255,0.1)", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)" }}>
          <p className="text-sm md:text-base text-white font-medium leading-relaxed">
            🇵🇰 <strong style={{ color: "#fbbf24" }}>پاکستان میں پہلی بار</strong> — کوئی اور رشتہ پلیٹ فارم نفسیاتی ہم آہنگی استعمال نہیں کرتا۔
          </p>
          <p className="text-xs md:text-sm text-gray-300 mt-2">
            Compatibility scores appear on every profile — helping you and your family make confident, informed decisions.
          </p>
          <Link
            to="/signup"
            className="inline-flex items-center justify-center gap-2 mt-4 px-7 py-3 rounded-full font-semibold text-sm transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.03]"
            style={{ backgroundColor: "#fbbf24", color: "#1a1a2e" }}
          >
            ابھی رجسٹر کریں — Register Quick
            <FaChevronRight className="text-xs" />
          </Link>
        </div>
      </div>
    </section>
  );
};

/* ─── Browse Profiles CTA ─────────────────────────────────────────────── */
const BrowseProfiles = () => (
  <section className="py-6 bg-white">
    <div className="max-w-[600px] mx-auto px-5 text-center">
      <Link
        to="/all-profiles"
        className="inline-flex items-center justify-center gap-3 px-10 py-4 rounded-full font-bold text-base transition-all duration-300 hover:scale-[1.03] shadow-md hover:shadow-lg"
        style={{ backgroundColor: C.emerald, color: C.white }}
      >
        <FaUsers className="text-xl" />
        تمام پروفائلز دیکھیں — View All Profiles
        <FaChevronRight className="text-sm" />
      </Link>
    </div>
  </section>
);

/* ─── Stories Section (photos — same as standard version) ─────────────── */
const StoriesSection = () => {
  const stories = [
    {
      image: "/images/stories/couple-1.webp",
      names: "Ahmed & Fatima",
      location: "Lahore → Lahore",
      text: '"As a doctor working 12-hour shifts, I never had time for the traditional rishta process. My mother found Select Proposal and within weeks, we connected with Fatima\'s family. Alhamdulillah, we\'re now happily married."',
    },
    {
      image: "/images/stories/couple-2.webp",
      names: "Zainab & Ali",
      location: "Karachi → Dubai",
      text: '"My parents were very particular about family background, and I wanted someone who shared my ambition. Select Proposal\'s filters made it easy for my family to find matches. Ali\'s profile stood out — educated, family-oriented, and now the best husband!"',
    },
    {
      image: "/images/stories/couple-3.webp",
      names: "Omar & Sana",
      location: "Islamabad → London",
      text: '"Being overseas, finding a genuine rishta back home felt impossible. Select Proposal\'s manual verification gave us confidence. When I saw Sana\'s verified profile, I knew this was different. 4 months later we had our Nikkah."',
    },
  ];

  return (
    <section className="py-6 md:py-8 bg-white">
      <div className="max-w-[1100px] mx-auto px-5">
        <div className="flex justify-between items-end mb-5 flex-wrap gap-3">
          <h2 className="font-playfair text-2xl md:text-[2rem] font-bold text-[#222]">
            Real People. Real Rishtas.
          </h2>
          <span className="text-xl md:text-2xl font-semibold font-urdu" style={{ color: C.emerald }}>
            حقیقی کہانیاں
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {stories.map((story, index) => (
            <div key={index} className="flex flex-col">
              <div className="relative rounded-xl overflow-hidden mb-4 aspect-[4/5] group">
                <img
                  src={story.image}
                  alt={story.names}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 text-white">
                  <h3 className="font-playfair text-lg font-bold text-white mb-0.5">
                    {story.names}
                  </h3>
                  <span className="text-sm opacity-90">{story.location}</span>
                </div>
              </div>
              <p className="italic text-[#555] text-sm leading-relaxed">
                {story.text}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── How It Works ────────────────────────────────────────────────────── */
const HowItWorks = () => {
  const steps = [
    { icon: <FaUserPlus className="w-5 h-5" />, numEn: "1", titleEn: "Register Quick", titleUr: "ابھی رجسٹر کریں", desc: "Create your profile with education, profession, and family details — takes 5 minutes.", color: C.emerald },
    { icon: <HiOutlineShieldCheck className="w-5 h-5" />, numEn: "2", titleEn: "We Approve Your Profile", titleUr: "ہم آپ کی تصدیق کریں گے", desc: "Our team manually reviews and approves every profile. Fake or incomplete profiles are rejected.", color: "#16803C" },
    { icon: <FaSearch className="w-5 h-5" />, numEn: "3", titleEn: "Browse Approved Profiles", titleUr: "منظور شدہ رشتے دیکھیں", desc: "Search verified profiles by education, profession, city, sect, and more.", color: C.emerald },
    { icon: <FaBinoculars className="w-5 h-5" />, numEn: "4", titleEn: "View Contact Details", titleUr: "رابطہ نمبر دیکھیں", desc: "Subscribe to a plan to see phone and WhatsApp numbers of verified profiles.", color: "#7C3AED" },
    { icon: <FaWhatsapp className="w-5 h-5" />, numEn: "5", titleEn: "Connect Directly", titleUr: "براہ راست رابطہ کریں", desc: "Contact families directly via WhatsApp or phone — no middleman involved.", color: "#25D366" },
  ];

  return (
    <section
      className="py-6 md:py-8 text-center"
      style={{ backgroundColor: C.goldLight }}
    >
      <div className="max-w-[1100px] mx-auto px-5">
        <span
          className="block text-xl md:text-2xl font-semibold mb-2 font-urdu" style={{ color: C.emerald }}
        >
          یہ کیسے کام کرتا ہے؟
        </span>
        <h2 className="font-playfair text-3xl md:text-[2.4rem] font-bold text-[#222] mb-6">
          How It Works
        </h2>

        {/* Desktop: horizontal */}
        <div className="hidden md:flex items-start justify-center gap-0 relative">
          {steps.map((s, i) => (
            <div key={i} className="flex-1 relative px-2 max-w-[210px]">
              {i < steps.length - 1 && (
                <div className="absolute top-[24px] left-[calc(50%+30px)] right-[calc(-50%+30px)] h-[2px]" style={{ backgroundColor: `${C.emerald}40` }} />
              )}
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 text-white shadow-md relative z-10"
                style={{ backgroundColor: s.color }}
              >
                {s.icon}
                <div className="absolute -top-1 -right-1 bg-white text-[#222] w-4 h-4 rounded-full text-[9px] flex items-center justify-center font-bold shadow-sm border border-gray-200">
                  {s.numEn}
                </div>
              </div>
              <h3 className="font-playfair text-sm font-bold text-[#222] mb-0.5">{s.titleEn}</h3>
              <p className="text-base mb-1.5 font-urdu" style={{ color: C.emeraldDk }}>{s.titleUr}</p>
              <p className="text-xs text-[#555] leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>

        {/* Mobile: vertical */}
        <div className="md:hidden flex flex-col items-start max-w-[380px] mx-auto">
          {steps.map((s, i) => (
            <div key={i} className="flex gap-4 relative pb-6 last:pb-0">
              {i < steps.length - 1 && (
                <div className="absolute left-[23px] top-[48px] bottom-0 w-[2px]" style={{ backgroundColor: `${C.emerald}40` }} />
              )}
              <div className="flex-shrink-0 relative z-10">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center text-white shadow-md"
                  style={{ backgroundColor: s.color }}
                >
                  {s.icon}
                  <div className="absolute -top-1 -right-1 bg-white text-[#222] w-4 h-4 rounded-full text-[9px] flex items-center justify-center font-bold shadow-sm border border-gray-200">
                    {s.numEn}
                  </div>
                </div>
              </div>
              <div className="text-left pt-0.5">
                <h3 className="font-playfair text-base font-bold text-[#222] mb-0">{s.titleEn}</h3>
                <p className="text-sm font-urdu mb-0.5" style={{ color: C.emeraldDk }}>{s.titleUr}</p>
                <p className="text-xs text-[#555] leading-relaxed">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── WhatsApp CTA Banner ─────────────────────────────────────────────── */
const WhatsAppCTA = () => {
  const { contactChannel, callEnabled, callNumber, callLink } = useSiteSettings();
  const ch = contactChannel("Hello! I want to register on Select Proposal");
  const isCall = ch.mode === "call";
  return (
    <section className="py-6 md:py-8 text-white text-center" style={{ backgroundColor: C.emerald }}>
      <div className="max-w-[700px] mx-auto px-5">
        <h2 className="text-2xl md:text-4xl font-semibold mb-2" style={{ fontFamily: "'Noto Naskh Arabic', serif", lineHeight: 1.6 }}>
          ابھی رابطہ کریں
        </h2>
        <h3 className="font-playfair text-xl md:text-2xl font-bold mb-2 text-white/90">
          {ch.titleEn}
        </h3>
        <p className="text-base text-white/80 mb-4 max-w-[500px] mx-auto">
          Have questions? Want help registering? Our team is ready to assist you in Urdu or English.
        </p>
        <a
          href={ch.href}
          {...(ch.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
          className="inline-flex items-center gap-3 px-10 py-4 rounded-full font-bold text-lg transition-all duration-300 hover:scale-[1.05] shadow-xl"
          style={{ backgroundColor: isCall ? C.gold : C.whatsapp, color: isCall ? C.dark : C.white }}
        >
          {isCall ? <FaPhoneAlt className="text-xl" /> : <FaWhatsapp className="text-2xl" />}
          {ch.labelUr} — {ch.labelEn}
        </a>
        {!isCall && callEnabled && (
          <p className="mt-3 mb-0 text-sm text-white/85">
            یا براہِ راست کال کریں —{" "}
            <a href={callLink()} className="font-semibold underline text-white hover:text-white/80">
              <FaPhoneAlt className="inline-block mr-1 text-xs" />
              {callNumber}
            </a>
          </p>
        )}
      </div>
    </section>
  );
};

/* ─── Final CTA ───────────────────────────────────────────────────────── */
const FinalCTA = () => {
  const { contactChannel, callEnabled, callNumber, callLink } = useSiteSettings();
  const freeOffered = useFreeOffered();
  const ch = contactChannel("Hello! I want to register on Select Proposal");
  const isCall = ch.mode === "call";
  return (
    <section
      className="py-8 md:py-10 text-center text-white relative overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${C.emerald} 0%, ${C.emeraldDk} 50%, ${C.dark} 100%)`,
        minHeight: "420px",
      }}
    >
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M20 20.5V18H0v-2h20v-2H0v-2h20v-2H0V8h20V6H0V4h20V2H0V0h22v20h2V0h2v20h2V0h2v20h2V0h2v20h2V0h2v20.5z'/%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      {/* ── Left couple (top-anchored) ── */}
      <div
        className="absolute top-0 left-0 pointer-events-none w-[24%] md:w-[22%] lg:w-[24%]"
        style={{ maxWidth: "360px", zIndex: 1 }}
      >
        <img
          src="/images/hero/couple-left.webp"
          alt=""
          className="w-full h-auto block"
          style={{
            filter: "brightness(0.88) saturate(0.8) contrast(1.08)",
            maskImage: "linear-gradient(to right, rgba(0,0,0,1) 45%, rgba(0,0,0,0) 100%), linear-gradient(to bottom, rgba(0,0,0,1) 50%, rgba(0,0,0,0) 100%)",
            WebkitMaskImage: "linear-gradient(to right, rgba(0,0,0,1) 45%, rgba(0,0,0,0) 100%), linear-gradient(to bottom, rgba(0,0,0,1) 50%, rgba(0,0,0,0) 100%)",
            maskComposite: "intersect",
            WebkitMaskComposite: "source-in",
          }}
          loading="lazy"
        />
      </div>

      {/* ── Right couple (top-anchored) ── */}
      <div
        className="absolute top-0 right-0 pointer-events-none w-[22%] md:w-[20%] lg:w-[22%]"
        style={{ maxWidth: "340px", zIndex: 1 }}
      >
        <img
          src="/images/hero/couple-right.webp"
          alt=""
          className="w-full h-auto block"
          style={{
            filter: "brightness(0.88) saturate(0.8) contrast(1.08)",
            maskImage: "linear-gradient(to left, rgba(0,0,0,1) 45%, rgba(0,0,0,0) 100%), linear-gradient(to bottom, rgba(0,0,0,1) 50%, rgba(0,0,0,0) 100%)",
            WebkitMaskImage: "linear-gradient(to left, rgba(0,0,0,1) 45%, rgba(0,0,0,0) 100%), linear-gradient(to bottom, rgba(0,0,0,1) 50%, rgba(0,0,0,0) 100%)",
            maskComposite: "intersect",
            WebkitMaskComposite: "source-in",
          }}
          loading="lazy"
        />
      </div>

      {/* golden glow */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: "40%", left: "50%", transform: "translate(-50%, -40%)",
          width: "700px", height: "700px",
          background: "radial-gradient(circle, rgba(212,168,67,0.05) 0%, transparent 70%)",
          zIndex: 0,
        }}
      />

      <div className="relative max-w-[600px] mx-auto px-5" style={{ zIndex: 2 }}>
        <div className="mb-4">
          <span className="inline-block text-[11px] md:text-xs font-bold tracking-[0.16em] uppercase" style={{ color: C.gold }}>
            JOIN THOUSANDS OF HAPPY FAMILIES
          </span>
        </div>
        <h2 className="text-2xl md:text-4xl font-semibold mb-3" style={{ fontFamily: "'Noto Naskh Arabic', serif", textShadow: "0 3px 25px rgba(0,0,0,0.4)", lineHeight: 1.6 }}>
          آج ہی اپنا رشتہ تلاش کریں
        </h2>
        <h3 className="font-playfair text-xl md:text-3xl font-bold mb-5 text-white/90" style={{ textShadow: "0 2px 20px rgba(0,0,0,0.3)" }}>
          Start Your Journey Today
        </h3>
        <p className="text-base text-white/75 mb-6 leading-relaxed" style={{ textShadow: "0 1px 10px rgba(0,0,0,0.2)" }}>
          Join Pakistani families who trust Select Proposal. {freeOffered ? "Free registration, verified" : "Verified"} profiles, complete privacy.
        </p>
        {/* WhatsApp (or direct-call) CTA */}
        <a
          href={ch.href}
          {...(ch.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
          className="inline-flex items-center gap-3 px-10 py-4 rounded-full font-bold text-lg transition-all duration-300 hover:scale-[1.05] shadow-xl mb-5"
          style={{ backgroundColor: isCall ? C.gold : C.whatsapp, color: isCall ? C.dark : C.white }}
        >
          {isCall ? <FaPhoneAlt className="text-xl" /> : <FaWhatsapp className="text-2xl" />}
          {ch.labelUr} — {ch.labelEn}
        </a>
        {!isCall && callEnabled && (
          <p className="-mt-2 mb-5 text-sm text-white/80">
            یا براہِ راست کال کریں —{" "}
            <a href={callLink()} className="font-semibold underline text-white hover:text-white/80">
              <FaPhoneAlt className="inline-block mr-1 text-xs" />
              {callNumber}
            </a>
          </p>
        )}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/signup"
            className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full font-semibold text-base transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.03]"
            style={{ backgroundColor: C.gold, color: C.dark }}
          >
            ابھی رجسٹر کریں — Register Quick
          </Link>
          <Link
            to="/pricing"
            className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full font-semibold text-base transition-all duration-300 border-2 border-white/40 hover:border-white/80 text-white"
          >
            پلانز دیکھیں — View Plans
          </Link>
        </div>
      </div>
    </section>
  );
};

/* ─── FAQ Section ─────────────────────────────────────────────────────── */
const HOME_FAQS = [
  {
    q: "کیا آپ ہمارے لیے رشتہ ڈھونڈتے ہیں؟ — Do you find matches for me?",
    a: "No. Select Proposal is a self-service platform, not a matchmaking or rishta agency. We manually verify every profile; you browse, shortlist and contact families directly. We do not mediate, negotiate or arrange meetings.",
  },
  {
    q: "Select Proposal کیا ہے؟ — What is Select Proposal?",
    a: "Select Proposal پاکستان کی سب سے قابل اعتماد آن لائن رشتہ اور شادی سروس ہے۔ Select Proposal is Pakistan's most trusted online rishta and matrimonial service. We connect families through verified profiles, advanced filters by education, city, profession, and education, and a safe, privacy-focused platform."
  },
  {
    q: "پروفائلز کی تصدیق کیسے ہوتی ہے؟ — How are profiles verified?",
    a: "ہر پروفائل کو ہماری ٹیم دستی طور پر جانچتی ہے۔ Every profile is manually screened by our team before approval. We verify identity documents (CNIC/ID card), education or professional details, and personal information. Fake or incomplete profiles are rejected to maintain quality."
  },
  {
    q: "کون سے شہروں میں رشتے دستیاب ہیں؟ — Which cities are covered?",
    a: "ہم پورے پاکستان میں رشتے فراہم کرتے ہیں۔ We have profiles from all major cities including Lahore, Karachi, Islamabad, Rawalpindi, Faisalabad, Multan, Peshawar, Quetta, Sialkot, and Gujranwala, plus overseas Pakistanis in UK, USA, Canada, UAE, and Australia."
  },
  {
    q: "کیا بیرون ملک پاکستانیوں کے لیے بھی رشتے ملتے ہیں؟ — Do you serve overseas Pakistanis?",
    a: "جی بالکل! Yes! We have a growing global community of overseas Pakistani profiles. No matter which country you're in, you can filter specifically for overseas profiles or find matches across both local and international communities."
  },
  {
    q: "کن بنیادوں پر رشتے تلاش کر سکتے ہیں؟ — What can I filter matches by?",
    a: "تعلیم، پیشہ، شہر اور عمر کی بنیاد پر۔ You can filter verified profiles by education, profession, city, age and family background. Select Proposal focuses on educated, verified profiles rather than caste-based matching."
  },
  {
    q: "رابطے کی معلومات کیسے دیکھیں؟ — How do I view contact details?",
    a: null // dynamically built from plans API
  },
  {
    q: "میری پرائیویسی محفوظ ہے؟ — Is my privacy protected?",
    a: "بالکل محفوظ ہے۔ Absolutely. Your contact details are hidden by default and only shown to approved members. Select Proposal is a no-photo platform — profiles are matched on education, profession, family and values, never on pictures. We never share your data with third parties. All communication happens through our secure platform."
  }
];

const buildContactFaqAnswer = (plans) => {
  const get = (key) => plans.find(p => p.key === key);
  const plus = get('select_plus_male');
  const gold = get('select_gold');
  const elite = get('select_elite');
  const plusV = plus ? plus.daily_views : 3;
  const goldV = gold ? gold.daily_views : 6;
  const eliteV = elite ? elite.daily_views : 10;
  return `مفت اکاؤنٹ سے آپ روزانہ 3 رابطے دیکھ سکتے ہیں۔ Free accounts can view up to 3 contacts per day. Select Plus allows ${plusV}/day, Gold allows ${goldV}/day, and Elite allows ${eliteV}/day. Contact details include phone number and WhatsApp so families can connect directly.`;
};

// Contact-views FAQ when Free is switched off: no free tier to describe.
const PAID_ONLY_CONTACT_FAQ = 'رابطے کی تفصیلات صرف پیڈ پلان پر نظر آتی ہیں۔ Anyone can browse profiles; contact details (phone and WhatsApp) are unlocked with a paid plan, so families can connect directly.';

const FAQSection = () => {
  const freeOffered = useFreeOffered();
  const [openIndex, setOpenIndex] = React.useState(null);
  const [faqs, setFaqs] = React.useState(HOME_FAQS);

  React.useEffect(() => {
    baseURL.get('/plans/config').then(res => {
      const plans = res.data?.data?.plans;
      if (plans && plans.length) {
        setFaqs(HOME_FAQS.map(f => f.a === null ? { ...f, a: buildContactFaqAnswer(plans) } : f));
      }
    }).catch(() => {
      // fallback to static text
      setFaqs(HOME_FAQS.map(f => f.a === null ? { ...f, a: 'مفت اکاؤنٹ سے آپ روزانہ 3 رابطے دیکھ سکتے ہیں۔ Free accounts can view up to 3 contacts per day. Select Plus allows 3/day, Gold allows 6/day, and Elite allows 10/day. Contact details include phone number and WhatsApp so families can connect directly.' } : f));
    });
  }, []);

  return (
    <section className="py-8 md:py-10 px-5" style={{ backgroundColor: '#FDFBF9' }}>
      <div className="max-w-3xl mx-auto">
        <h2
          className="font-playfair text-center text-3xl md:text-4xl font-bold mb-2"
          style={{ color: C.dark }}
        >
          Frequently Asked Questions
        </h2>
        <p className="text-center text-gray-500 mb-6 text-sm">
          Everything you need to know about Select Proposal
        </p>
        <div className="space-y-3">
          {faqs.map((faq, idx) => (
            <div
              key={idx}
              className="rounded-xl overflow-hidden transition-all duration-200"
              style={{
                border: `1px solid ${openIndex === idx ? C.emerald + '40' : '#e8e0d8'}`,
                backgroundColor: openIndex === idx ? '#f8f6f3' : 'white',
              }}
            >
              <button
                onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
                className="w-full flex items-center justify-between px-5 py-4 text-left"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                <span
                  className="font-semibold text-sm md:text-base pr-4"
                  style={{ color: C.dark, lineHeight: '1.5' }}
                >
                  {faq.q}
                </span>
                <span
                  className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold transition-transform duration-200"
                  style={{
                    backgroundColor: openIndex === idx ? C.emerald : '#f0ebe6',
                    color: openIndex === idx ? 'white' : C.dark,
                    transform: openIndex === idx ? 'rotate(45deg)' : 'none',
                  }}
                >
                  +
                </span>
              </button>
              {openIndex === idx && (
                <div
                  className="px-5 pb-4 text-sm leading-relaxed"
                  style={{ color: '#555', fontFamily: "'Inter', sans-serif" }}
                >
                  {!freeOffered && String(faq.a || '').startsWith('مفت اکاؤنٹ') ? PAID_ONLY_CONTACT_FAQ : faq.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── Main Pakistan Homepage ──────────────────────────────────────────── */
// Moving Abroad Section
const MovingAbroadSection = () => {
  const navigate = useNavigate();
  const freeOffered = useFreeOffered();
  return (
    <section className="py-12 px-4" style={{ background: 'linear-gradient(135deg, #fef7f7 0%, #f0f4ff 100%)' }}>
      <div className="max-w-5xl mx-auto text-center">
        <IconBadge size={70}>
          <PlaneGraphic size={42} />
        </IconBadge>
        <h2
          className="text-2xl md:text-3xl font-bold mt-3 mb-2"
          style={{ fontFamily: "'Playfair Display', serif", color: '#8B1E1E' }}
        >
          Bahar Ja Rahe Hain? Pehle Rishta Dhoondein
        </h2>
        <p className="text-gray-600 mb-2" style={{ fontFamily: "'Inter', sans-serif", fontSize: '15px' }}>
          Study ya kaam ke liye bahar ja rahe hain?
        </p>
        <p
          className="text-gray-500 text-sm mb-8 max-w-xl mx-auto"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          Kisi bhi mulk mein ja rahe hon — apni destination city mein jaane wale rishte se match karein. Nayi zindagi saath shuru karein.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
          {[
            { icon: 'graduation', title: 'Students & Professionals', desc: 'Study ya kaam ke liye ja rahe hain? Apni journey share karne wala partner dhoondein.' },
            { icon: 'destination', title: 'Same City Matching', desc: 'Destination city ke hisaab se filter karein — same shehar wale se connect hon.' },
            { icon: 'rings', title: 'Naye Mulk Mein Saath', desc: 'Apne values share karne wale partner ke saath nayi zindagi shuru karein.' },
          ].map((item, i) => (
            <div
              key={i}
              className="bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow flex flex-col items-center text-center"
              style={{ border: '1px solid #f0e0e0' }}
            >
              <IconBadge size={60}>
                  {React.createElement(MOVING_ABROAD_GRAPHICS[item.icon], { size: 36 })}
                </IconBadge>
              <h3
                className="text-base font-semibold mt-2 mb-1"
                style={{ fontFamily: "'Inter', sans-serif", color: '#333' }}
              >
                {item.title}
              </h3>
              <p className="text-gray-500 text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>
                {item.desc}
              </p>
            </div>
          ))}
        </div>

        <div className="flex justify-center">
          <button
            onClick={() => navigate('/signup')}
            className="px-6 py-2.5 rounded-lg text-white font-semibold text-sm hover:opacity-90 transition-opacity"
            style={{ backgroundColor: '#8B1E1E', fontFamily: "'Inter', sans-serif" }}
          >
            {freeOffered ? "Free Register Karein" : "Register Karein"}
          </button>
        </div>
      </div>
    </section>
  );
};

const PakistanHomePage = ({ setLoginPopUp }) => {
  const fontReady = useUrduFontReady();
  return (
    <main
      className="transition-opacity duration-200"
      style={{ opacity: fontReady ? 1 : 0 }}
    >
      <SEO
        title="Select Proposal — پاکستان کی سب سے قابل اعتماد رشتہ سروس | Rishta Service Pakistan"
        description="پاکستان کی سب سے قابل اعتماد شادی اور رشتہ سروس۔ Find your perfect rishta on Select Proposal. Verified profiles from Lahore, Karachi, Islamabad & all cities. Free registration."
        path="/"
        jsonLd={[serviceSchema, howToSchema, faqSchema(HOME_FAQS.map(f => ({ question: f.q, answer: f.a })))]}
        hreflangEn="/"
        hreflangUr="/"
        breadcrumbs={[{ name: "Home", path: "/" }]}
      />
      <UrduNewsTicker />
      <HeroSection />
      <NotARishtaBureau variant="ur" accent="#047857" />
      <YouTubeVideoSection />
      <CompatibilitySection />
      <BrowseProfiles />
      <StoriesSection />
      <HowItWorks />
      <WhatsAppCTA />
      <TrustBadges />
      <FeaturesSection />
      <ProfileVerify />
      <FAQSection />
      <MovingAbroadSection />
      <FinalCTA />
    </main>
  );
};

export default PakistanHomePage;
